import React from "react";
import { Download, X, Smartphone, QrCode } from "lucide-react";
import { siteInfo } from "../mock";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [showIOS, setShowIOS] = React.useState(false);
  const [installed, setInstalled] = React.useState(false);

  React.useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    const dismissed = localStorage.getItem("3e_pwa_dismissed");

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!dismissed) {
        setTimeout(() => setOpen(true), 2500);
      }
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    const onInstalled = () => { setInstalled(true); setOpen(false); };
    window.addEventListener("appinstalled", onInstalled);

    // iOS fallback (no beforeinstallprompt)
    if (isIOS() && !dismissed) {
      setTimeout(() => { setShowIOS(true); setOpen(true); }, 3000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") setInstalled(true);
    setDeferred(null);
    setOpen(false);
  };

  const dismiss = () => {
    try { localStorage.setItem("3e_pwa_dismissed", "1"); } catch {}
    setOpen(false);
  };

  if (installed || !open) return null;

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&bgcolor=ffffff&color=0b1220&margin=4&data=${encodeURIComponent(window.location.origin)}`;

  return (
    <div className="fixed left-4 right-4 bottom-4 md:left-6 md:right-auto md:bottom-6 z-50 md:max-w-xl">
      <div className="card-soft shadow-2xl p-5 md:p-6 relative overflow-hidden" style={{ background: "#0b1324" }}>
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#c7e34a]/10 blur-3xl pointer-events-none" />
        <button onClick={dismiss} aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-slate-300 flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-4 md:gap-5">
          <div className="hidden sm:block shrink-0 w-24 h-24 rounded-xl bg-white p-1">
            <img src={qrSrc} alt="Scan to install" className="w-full h-full object-contain rounded-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[#c7e34a] text-xs uppercase tracking-widest font-semibold mb-2">
              <Smartphone className="w-3.5 h-3.5" /> Keep {siteInfo.brand} on your phone
            </div>
            <h4 className="font-serif-display text-white text-2xl md:text-3xl leading-tight">Install for quick access.</h4>
            <p className="text-slate-300 text-sm mt-2 pr-8">
              {showIOS
                ? "On iPhone, tap the Share button, then choose ‘Add to Home Screen’."
                : "Scan the QR code with your phone camera, or use your browser menu and choose ‘Install app’ or ‘Add to Home Screen’."}
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-4">
              {deferred && !showIOS && (
                <button onClick={install} className="btn-lime">
                  <Download className="w-4 h-4" /> How to install
                </button>
              )}
              {showIOS && (
                <span className="inline-flex items-center gap-2 text-[#c7e34a] text-sm">
                  <QrCode className="w-4 h-4" /> Scan with your phone camera
                </span>
              )}
              <button onClick={dismiss} className="text-sm text-slate-400 hover:text-white">Not now</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
